import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Command } from 'cmdk';
import { Popover } from 'radix-ui';
import { Check, Plus } from 'lucide-react';
import type { Label, Project, ProjectColor } from '@timato/shared';
import { ColorSwatches, Modal } from '@/components/ui';
import { useLabels } from '@/data/hooks';
import { findOrCreateLabel, projectLabelIds, updateLabel } from '@/data/labels';
import { cn } from '@/lib/cn';
import { colorVar } from '@/lib/projectColors';
import { LabelChip } from './LabelChip';

type Props = {
  value: string[]; onChange: (ids: string[]) => void; align?: 'start' | 'end'; compact?: boolean;
  /** The trigger drops its word once a label is chosen, leaving a small "+" after the chips: for the timer screen. */
  quiet?: boolean;
  /**
   * The project the thing being labelled belongs to: its labels (`projectLabelIds`) are the list, and a search adds
   * every other label below them. `null` is "no project", which lists the labels no project uses. Leave it out where
   * there is no such thing (choosing a project's own labels), and every label is listed.
   */
  project?: Pick<Project, 'id' | 'name' | 'labelIds'> | null;
};
const row = 'flex items-center gap-[9px] px-[11px] py-[9px] text-[12.5px] cursor-pointer select-none border-t border-row-divider first:border-t-0 data-[selected=true]:bg-inset';

/**
 * "Search or create…" with checkmarks and a per-row tint square (8e). Given a project, its labels come first and a search
 * lists the rest under them, so a label from elsewhere is reused rather than typed again as a duplicate. Without one,
 * team labels group before personal ones. The create row always makes a personal label, which joins the project by
 * being put on something in it.
 */
export function LabelPicker({ value, onChange, align = 'start', compact = false, quiet = false, project }: Props) {
  const labels = useLabels().filter((l) => !l.retiredAt);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tinting, setTinting] = useState<Label | null>(null);
  const q = query.trim().toLowerCase();
  const scoped = project !== undefined;
  // Read only while the list is open: "no project" has to look at every session to answer.
  const ownIds = useLiveQuery(() => (open && scoped ? projectLabelIds(project) : undefined), [open, scoped, project?.id, project?.labelIds.join()]);
  // What is already chosen stays in the project's group, so a label picked from further down does not jump about.
  const own = ownIds && labels.filter((l) => ownIds.has(l.id) || value.includes(l.id));
  const matching = (items: Label[]) => items.filter((l) => !q || l.name.toLowerCase().includes(q));
  const groups = (!scoped
    ? [{ heading: 'Team', items: labels.filter((l) => l.teamId) }, { heading: 'Personal', items: labels.filter((l) => !l.teamId) }]
    : own ? [{ heading: project?.name ?? 'Not in a project', items: own }, ...(q ? [{ heading: project ? 'Other projects' : 'In projects', items: labels.filter((l) => !own.includes(l)) }] : [])]
    : [] // still reading: an empty list for a moment beats a wrong one
  ).map((g) => ({ ...g, items: matching(g.items) })).filter((g) => g.items.length);
  const exact = labels.some((l) => l.name.trim().toLowerCase() === q);
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const create = async () => { if (!q) return; const l = await findOrCreateLabel(query); if (!value.includes(l.id)) onChange([...value, l.id]); setQuery(''); };
  const selected = value.map((id) => labels.find((l) => l.id === id)).filter((l): l is Label => !!l);

  const item = (l: Label) => {
    const on = value.includes(l.id);
    return (
      <Command.Item key={l.id} value={l.id} onSelect={() => toggle(l.id)} className={row}>
        <span className={cn('w-[15px] h-[15px] rounded-[4px] grid place-items-center shrink-0', on ? 'text-on-project' : 'border-[1.5px] border-chip-border')}
          style={on ? { background: l.tint ? colorVar(l.tint) : 'var(--color-accent)' } : undefined}>
          {on && <Check size={10} strokeWidth={4} />}
        </span>
        <span className="flex-1 font-semibold truncate">{l.name}</span>
        <button type="button" aria-label={`Tint ${l.name}`} onClick={(e) => { e.stopPropagation(); setTinting(l); }}
          className="w-[9px] h-[9px] rounded-[3px] border border-chip-border p-0 cursor-pointer shrink-0" style={l.tint ? { background: colorVar(l.tint), borderColor: colorVar(l.tint) } : undefined} />
      </Command.Item>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      {!compact && selected.map((l) => <LabelChip key={l.id} label={l} onRemove={() => toggle(l.id)} />)}
      <Popover.Root open={open} onOpenChange={(o) => { setOpen(o); if (o) setQuery(''); }}>
        <Popover.Trigger asChild>
          <button type="button" aria-label="Labels" className="inline-flex items-center gap-1 rounded-[5px] border border-dashed border-chip-border px-2 py-[3px] text-[11.5px] font-bold text-ink-2 bg-transparent cursor-pointer hover:text-ink">
            <Plus size={11} />{compact ? (value.length || '') : quiet && selected.length ? '' : 'Label'}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align={align} sideOffset={6} className="z-50 w-[260px] rounded-input border border-chip-border bg-canvas shadow-popover overflow-hidden outline-none">
            <Command shouldFilter={false} loop label="Labels">
              <div className="px-[11px] py-[9px] border-b border-row-divider">
                <Command.Input autoFocus value={query} onValueChange={setQuery} placeholder="Search or create…"
                  className="w-full h-8 px-[9px] rounded-[7px] border border-chip-border bg-transparent outline-none text-[12.5px] font-semibold text-ink placeholder:text-ink-2" />
              </div>
              <Command.List className="max-h-[260px] overflow-y-auto">
                {q && !exact && (
                  <Command.Item value={`create:${q}`} onSelect={() => void create()} className={row}>
                    <span className="w-[15px] h-[15px] rounded-[4px] bg-primary-btn-bg text-primary-btn-fg grid place-items-center shrink-0"><Plus size={10} strokeWidth={3} /></span>
                    <span className="flex-1 truncate">Create <b>“{query.trim()}”</b></span>
                  </Command.Item>
                )}
                {groups.map((g) => (
                  <Command.Group key={g.heading} heading={scoped || groups.length > 1 ? g.heading : undefined}
                    className="[&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-extrabold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[.07em] [&_[cmdk-group-heading]]:text-ink-2 [&_[cmdk-group-heading]]:px-[11px] [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1">
                    {g.items.map(item)}
                  </Command.Group>
                ))}
                {!groups.length && !q && (!scoped || own) && <Command.Empty className="px-[11px] py-3 text-[12.5px] text-ink-2">{!labels.length ? 'No labels yet - type to create one.' : `${project ? `None in ${project.name} yet. ` : scoped ? 'Every label is in a project. ' : ''}Type to search all ${labels.length} labels, or to create one.`}</Command.Empty>}
                {own && !q && own.length > 0 && labels.length > own.length && <p className="px-[11px] py-2 m-0 text-[11.5px] text-ink-2 border-t border-row-divider">Type to search all {labels.length} labels.</p>}
              </Command.List>
            </Command>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {tinting && (
        <Modal open onOpenChange={(o) => !o && setTinting(null)} size="sm" title={`Tint “${tinting.name}”`} description="Any project colour, including your own.">
          <div className="flex items-center gap-4">
            <ColorSwatches allowNone value={tinting.tint} onChange={(t) => { void updateLabel(tinting, { tint: t }); setTinting({ ...tinting, tint: t as ProjectColor | null }); }} label="Label tint" />
            <LabelChip label={tinting} />
          </div>
        </Modal>
      )}
    </div>
  );
}
